import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Divider } from '@ui/components/Divider'
import { Modal } from '@ui/components/Modal'
import { Radio } from '@ui/components/Radio'

import { PageData } from '@utils/ApiClient'
import {
  TradingAccountRestrictions,
  isRestricted as taIsRestricted,
} from '@utils/trading-account.utils'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { TradingAccount } from '@model/TradingAccount'
import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

import { Balance } from '../Balance'
import { WalletOrTradingAccountType } from '../TransferForm'

export const isWalletType = (x: WalletOrTradingAccountType): x is WalletDto =>
  'walletType' in x || 'walletState' in x

export const isTradingAccountType = (x: WalletOrTradingAccountType): x is TradingAccount =>
  'platformType' in x || 'leverage' in x

interface RadioBoxProps {
  isDisabled: boolean
  tradingAccountOrWallet: WalletOrTradingAccountType
  checked: boolean
  onTransferToChange(next: WalletOrTradingAccountType): void
  caption?: React.ReactNode
  name?: string
}

export const RadioBoxField: React.FC<RadioBoxProps> = ({
  isDisabled,
  onTransferToChange,
  tradingAccountOrWallet,
  checked,
  caption,
  name = 'transferTo',
}) => {
  const { t } = useTranslation()

  const getWalletFullName = (wallet: WalletDto) =>
    wallet.walletType.id === WalletTypeEnum.IB
      ? t('Wallet.Currency IB Wallet', { walletCurrencyId: wallet.currency.id })
      : t('Wallet.Currency Wallet', { walletCurrencyId: wallet.currency.id })

  const label = isWalletType(tradingAccountOrWallet)
    ? getWalletFullName(tradingAccountOrWallet)
    : (tradingAccountOrWallet.name as React.ReactNode)

  return (
    <Radio
      name={name}
      disabled={isDisabled}
      checked={checked}
      onChange={() => !isDisabled && onTransferToChange(tradingAccountOrWallet)}
      caption={caption}
    >
      {label}
    </Radio>
  )
}

interface Props {
  wallets: WalletDto[]
  tradingAccounts: PageData<TradingAccount> | undefined
  onTransferToChange(transferTo: WalletOrTradingAccountType): void
  transferFrom: WalletOrTradingAccountType
  transferTo: WalletOrTradingAccountType
}

export const TransferToModal: BaseModalType<Props> = ({
  wallets,
  tradingAccounts,
  onTransferToChange,
  transferFrom,
  transferTo,
  close,
}) => {
  const { t } = useTranslation()

  return (
    <Modal>
      <Modal.Header>{t('Wallet.Transfer To')}</Modal.Header>
      <Modal.Body>
        {wallets.map((wallet) => {
          const hasTransferToRestriction = isRestricted(
            WalletRestrictions.TRANSFER_TO,
            wallet.restrictions
          )

          const isSameAsFromWallet = isWalletType(transferFrom) && wallet.id === transferFrom.id
          const isSameCurrencyAsFrom = transferFrom?.currency?.id === wallet.currency.id

          const radioDisabled = isWalletType(transferFrom)
            ? hasTransferToRestriction || isSameAsFromWallet
            : isTradingAccountType(transferFrom)
            ? hasTransferToRestriction || !isSameCurrencyAsFrom
            : hasTransferToRestriction

          const checked = transferTo?.id === wallet.id

          return [
            <RadioBoxField
              key={`w-${wallet.id}`}
              tradingAccountOrWallet={wallet}
              isDisabled={radioDisabled}
              checked={checked}
              onTransferToChange={onTransferToChange}
              caption={
                <Balance
                  balance={wallet.balance}
                  currencyId={wallet.currency.id}
                  isWallet
                  isDisabled={hasTransferToRestriction}
                  isTransferTo
                  onClose={close}
                />
              }
            />,

            tradingAccounts?.items
              .filter((ta) => ta.currency.id === wallet.currency.id)
              .map((ta) => {
                const taHasRestriction = taIsRestricted(
                  TradingAccountRestrictions.TRANSFER_TO,
                  ta.restrictions
                )

                const showForThisWallet =
                  isWalletType(transferFrom) && transferFrom.id === ta.wallet.id

                if (!showForThisWallet) return null

                const taChecked = transferTo?.id === ta.id

                return [
                  <Divider.Horizontal key={`div-${ta.id}`} />,
                  <Box key={`box-${ta.id}`} pl='x24'>
                    <RadioBoxField
                      tradingAccountOrWallet={ta}
                      isDisabled={taHasRestriction}
                      checked={taChecked}
                      onTransferToChange={onTransferToChange}
                      caption={
                        <Balance
                          platformType={ta.platformType}
                          accountType={ta.accountType}
                          balance={ta.platformOverview.balance}
                          currencyId={ta.currency.id}
                          freeMargin={ta.platformOverview.freeMargin}
                          isWallet={false}
                          isDisabled={taHasRestriction}
                          isTransferTo
                          onClose={close}
                        />
                      }
                    />
                  </Box>,
                ]
              }),
          ]
        })}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}
